import { ActionIcon, Box, Group, Slider, Text, Tooltip } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerSkipForward,
} from "@tabler/icons-react";
import type { PercentagePoint } from "types/testTypes";
import type {
  ReadingFixationTransitionDirection,
  ReadingFixationDisplayCategory,
  ReadingFixationRecord,
} from "types/tests";

export type ReadingLayoutRect01 = { x: number; y: number; w: number; h: number };
export type ReadingLayoutWord = { t: string; i: number; r: ReadingLayoutRect01 };
export type ReadingLayoutPage = {
  index: number;
  text?: string;
  words: ReadingLayoutWord[];
  layoutAspect: number;
};
export type ReadingLayoutConfig = {
  fontSize: number;
  wordSpacing: number;
  lineHeight: number;
  maxLineWidth: number;
  aoiPadding: number;
  cameraToLayoutDistance?: number;
  textWindowHeight?: number;
};
export type ReadingLayoutManifest = {
  layout: ReadingLayoutConfig;
  pages: ReadingLayoutPage[];
  textOrientation?: "L2R" | "R2L";
};

export type ReadingLayoutEyeSample = {
  t: number;
  xPercentage: number;
  yPercentage: number;
};

type EstimatedTextLine = {
  index: number;
  centerY01: number;
  top01: number;
  bottom01: number;
  left01: number;
  right01: number;
  wordCount: number;
};

export type ReadingLayoutRendererProps = {
  layout: ReadingLayoutConfig;
  pages: ReadingLayoutPage[];
  fixationRecordsByPageIndex?: Record<number, ReadingFixationRecord[]>;
  samplesByPageIndex?: Record<number, ReadingLayoutEyeSample[]>;
  renderPageHeader?: (page: ReadingLayoutPage) => React.ReactNode;
  renderMode?: "fixationSaccade" | "raw";
  textOrientation?: "L2R" | "R2L";
  showEstimatedLines?: boolean;
  maxContentWidthPx?: number;
  contentWidthRatio?: number;
  contentPaddingPx?: number;
  showFixationSlider?: boolean;
  wordBackgroundByIndex?: Record<number, Record<number, string>>;
};

const WORD_FONT_STACK = 'Helvetica, "Noto Sans JP", sans-serif';
const DEFAULT_FALLBACK_PAGE_ASPECT = 16 / 9;
const DEFAULT_MIN_CONTENT_WIDTH = 0;
const DEFAULT_MAX_CONTENT_WIDTH = 900;
const DEFAULT_CONTENT_WIDTH_RATIO = 0.95;
const DEFAULT_FALLBACK_CONTENT_WIDTH = 600;
const DEFAULT_FALLBACK_PAGE_HEIGHT = 320;
const MIN_LINE_CLUSTER_TOLERANCE_01 = 0.004;
const LINE_CLUSTER_TOLERANCE_FACTOR = 0.7;

export default function ReadingLayoutRenderer({
  layout,
  pages,
  fixationRecordsByPageIndex,
  samplesByPageIndex,
  renderPageHeader,
  renderMode = "fixationSaccade",
  textOrientation = "L2R",
  showEstimatedLines = false,
  maxContentWidthPx = DEFAULT_MAX_CONTENT_WIDTH,
  contentWidthRatio = DEFAULT_CONTENT_WIDTH_RATIO,
  contentPaddingPx = 0,
  showFixationSlider = false,
  wordBackgroundByIndex,
}: ReadingLayoutRendererProps) {
  const safePages = useMemo(() => (Array.isArray(pages) ? pages : []), [pages]);
  const multiPage = safePages.length > 1;

  if (!safePages.length) {
    return (
      <Text size="sm" c="dimmed">
        No layout pages.
      </Text>
    );
  }

  return (
    <Box
      className={multiPage ? "reading-layout-multipage" : undefined}
      style={{ display: "flex", flexDirection: "column", gap: 24, width: "100%" }}
    >
      {multiPage && (
        <style>{`
          .printable-section:has(.reading-layout-multipage) {
            break-inside: auto !important;
            overflow: visible !important;
          }
          .printable-section:has(.reading-layout-multipage) .mantine-Card-root {
            overflow: visible !important;
          }
        `}</style>
      )}
      {safePages.map((page, i) => (
        <Box
          key={page.index}
          style={multiPage && i < safePages.length - 1 ? { breakAfter: "page" } : undefined}
        >
          <ReadingLayoutPageCanvas
            layout={layout}
            page={page}
            fixationRecords={fixationRecordsByPageIndex?.[page.index] ?? []}
            samples={samplesByPageIndex?.[page.index] ?? []}
            renderHeader={renderPageHeader}
            renderMode={renderMode}
            textOrientation={textOrientation}
            showEstimatedLines={showEstimatedLines}
            maxContentWidthPx={maxContentWidthPx}
            contentWidthRatio={contentWidthRatio}
            contentPaddingPx={contentPaddingPx}
            showFixationSlider={showFixationSlider}
            wordBackgrounds={wordBackgroundByIndex?.[page.index]}
          />
        </Box>
      ))}
    </Box>
  );
}

function ReadingLayoutPageCanvas({
  layout,
  page,
  fixationRecords,
  samples,
  renderHeader,
  renderMode,
  textOrientation,
  showEstimatedLines,
  maxContentWidthPx,
  contentWidthRatio,
  contentPaddingPx,
  showFixationSlider,
  wordBackgrounds,
}: {
  layout: ReadingLayoutConfig;
  page: ReadingLayoutPage;
  fixationRecords: ReadingFixationRecord[];
  samples: ReadingLayoutEyeSample[];
  renderHeader?: (page: ReadingLayoutPage) => React.ReactNode;
  renderMode: "fixationSaccade" | "raw";
  textOrientation: "L2R" | "R2L";
  showEstimatedLines: boolean;
  maxContentWidthPx: number;
  contentWidthRatio: number;
  contentPaddingPx: number;
  showFixationSlider: boolean;
  wordBackgrounds?: Record<number, string>;
}) {
  const { ref: outerRef, width: outerW } = useElementSize();
  const words = useMemo(() => page.words ?? [], [page.words]);
  const orderedSamples = useMemo(() => orderSamples(samples), [samples]);

  const totalFixations = fixationRecords.length;
  const [sliderValue, setSliderValue] = useState(totalFixations);
  const prevTotalRef = useRef(totalFixations);

  // Reset slider when fixation data changes (e.g. different test loaded)
  if (prevTotalRef.current !== totalFixations) {
    prevTotalRef.current = totalFixations;
    setSliderValue(totalFixations);
  }

  const visibleFixations = useMemo(
    () => (sliderValue >= totalFixations ? fixationRecords : fixationRecords.slice(0, sliderValue)),
    [fixationRecords, sliderValue, totalFixations],
  );

  const onSliderChange = useCallback((v: number) => setSliderValue(v), []);

  const pageAspect = useMemo(() => {
    const layoutWidth = typeof layout.maxLineWidth === "number" ? layout.maxLineWidth : 0;
    const layoutHeight = typeof layout.textWindowHeight === "number" ? layout.textWindowHeight : 0;
    if (Number.isFinite(page.layoutAspect) && page.layoutAspect > 0) return page.layoutAspect;
    if (layoutWidth > 0 && layoutHeight > 0) {
      return layoutWidth / layoutHeight;
    }
    return DEFAULT_FALLBACK_PAGE_ASPECT;
  }, [layout.maxLineWidth, layout.textWindowHeight, page.layoutAspect]);

  const outerPaddingPx = useMemo(
    () => Math.max(0, Math.floor(contentPaddingPx || 0)),
    [contentPaddingPx],
  );
  const contentWidthPx = useMemo(
    () => computeContentWidth(outerW, contentWidthRatio, maxContentWidthPx),
    [outerW, contentWidthRatio, maxContentWidthPx],
  );
  const pageWidthPx = useMemo(
    () => Math.max(1, contentWidthPx - outerPaddingPx * 2),
    [contentWidthPx, outerPaddingPx],
  );
  const pageHeightPx = useMemo(
    () => computePageHeight(pageWidthPx, pageAspect),
    [pageWidthPx, pageAspect],
  );

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const geometry = useMemo(() => {
    const layoutWidth =
      typeof layout.maxLineWidth === "number" && layout.maxLineWidth > 0 ? layout.maxLineWidth : 1;
    const layoutHeight =
      typeof layout.textWindowHeight === "number" && layout.textWindowHeight > 0
        ? layout.textWindowHeight
        : layoutWidth / pageAspect;
    const scale = pageWidthPx / layoutWidth;
    return {
      layoutWidth,
      layoutHeight,
      scale,
      fontSizePx: layout.fontSize * scale,
      lineHeightPx: layout.lineHeight * scale,
    };
  }, [
    layout.fontSize,
    layout.lineHeight,
    layout.maxLineWidth,
    layout.textWindowHeight,
    pageAspect,
    pageWidthPx,
  ]);
  const wordBoxMedianHeightPx = useMemo(
    () => computeWordBoxMedianHeightPx(words, pageHeightPx),
    [pageHeightPx, words],
  );
  const estimatedLines = useMemo(
    () => estimateTextLines(words, textOrientation),
    [textOrientation, words],
  );

  const isRtl = textOrientation === "R2L";
  const wordRenderInfos = useMemo(
    () =>
      words.map((word) => {
        const css = rectToCss(word.r, pageWidthPx, pageHeightPx);
        const fontSizePx = Math.max(1, Math.min(geometry.fontSizePx, css.height * 0.78));
        const naturalWidthPx = isRtl ? measureTextWidth(word.t, fontSizePx) : null;
        const widthScale =
          naturalWidthPx && css.width > 0 && naturalWidthPx > css.width
            ? css.width / naturalWidthPx
            : 1;
        return { word, css, fontSizePx, widthScale };
      }),
    [words, geometry.fontSizePx, pageWidthPx, pageHeightPx, isRtl],
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    if (!pageWidthPx || !pageHeightPx) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const widthPx = Math.max(1, Math.round(pageWidthPx * dpr));
    const heightPx = Math.max(1, Math.round(pageHeightPx * dpr));

    canvasRef.current.width = widthPx;
    canvasRef.current.height = heightPx;
    canvasRef.current.style.width = `${pageWidthPx}px`;
    canvasRef.current.style.height = `${pageHeightPx}px`;

    ctx.resetTransform();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, pageWidthPx, pageHeightPx);

    if (showEstimatedLines && estimatedLines.length) {
      drawEstimatedLines(ctx, estimatedLines, pageWidthPx, pageHeightPx);
    }

    if (renderMode === "raw") {
      if (!orderedSamples.length) return;

      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = "rgba(0, 123, 255, 0.35)";

      orderedSamples.forEach((sample, idx) => {
        const pos = percentagePointToCanvas(sample, pageWidthPx, pageHeightPx);
        if (idx === 0) {
          ctx.beginPath();
          ctx.moveTo(pos.x, pos.y);
        } else {
          ctx.lineTo(pos.x, pos.y);
        }
      });

      ctx.stroke();

      const dotRadius = Math.max(2, Math.round(wordBoxMedianHeightPx * 0.16));
      ctx.fillStyle = "rgba(0, 123, 255, 0.55)";

      orderedSamples.forEach((sample) => {
        const pos = percentagePointToCanvas(sample, pageWidthPx, pageHeightPx);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, dotRadius, 0, Math.PI * 2);
        ctx.fill();
      });

      return;
    }

    if (!visibleFixations.length) return;

    drawSaccadeLines(ctx, visibleFixations, pageWidthPx, pageHeightPx);
    drawFixationCircles(
      ctx,
      visibleFixations,
      fixationRecords,
      pageWidthPx,
      pageHeightPx,
      wordBoxMedianHeightPx,
    );
  }, [
    visibleFixations,
    orderedSamples,
    estimatedLines,
    pageHeightPx,
    pageWidthPx,
    renderMode,
    showEstimatedLines,
    wordBoxMedianHeightPx,
  ]);

  return (
    <Box style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box
        style={{
          width: "100%",
          maxWidth:
            Number.isFinite(maxContentWidthPx) && maxContentWidthPx > 0
              ? `${maxContentWidthPx}px`
              : undefined,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {renderHeader ? (
          renderHeader(page)
        ) : (
          <Text fw={600} c="dimmed">
            Paragraph {page.index + 1}
          </Text>
        )}

        <Box ref={outerRef} style={{ width: "100%" }}>
          <Box
            style={{
              width: pageWidthPx,
              marginLeft: "auto",
              marginRight: "auto",
              padding: outerPaddingPx,
            }}
          >
            <Box
              pos="relative"
              style={{
                width: pageWidthPx,
                height: pageHeightPx,
                background: "var(--oculera-surface)",
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  inset: 0,
                  direction: textOrientation === "R2L" ? "rtl" : "ltr",
                }}
              >
                {wordRenderInfos.map(({ word, css, fontSizePx, widthScale }) => {
                  const wordBg = wordBackgrounds?.[word.i];
                  return (
                    <Box
                      key={`${page.index}-${word.i}-${word.t}`}
                      style={{
                        position: "absolute",
                        left: css.left,
                        top: css.top,
                        width: css.width,
                        height: css.height,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: fontSizePx,
                        lineHeight: `${Math.min(geometry.lineHeightPx, css.height)}px`,
                        fontFamily: WORD_FONT_STACK,
                        color: "var(--oculera-text)",
                        whiteSpace: "pre",
                        overflow: "visible",
                        userSelect: "none",
                        pointerEvents: "none",
                        backgroundColor: wordBg,
                        borderRadius: wordBg ? 2 : undefined,
                        transition: wordBg ? "background-color 0.3s ease" : undefined,
                      }}
                    >
                      {widthScale !== 1 ? (
                        <span
                          style={{
                            display: "inline-block",
                            whiteSpace: "pre",
                            transform: `scaleX(${widthScale})`,
                            transformOrigin: "center",
                          }}
                        >
                          {word.t}
                        </span>
                      ) : (
                        word.t
                      )}
                    </Box>
                  );
                })}
              </Box>

              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "block",
                  width: pageWidthPx,
                  height: pageHeightPx,
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>
        </Box>

        {renderMode === "fixationSaccade" ? (
          <Box
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              style={{
                width: pageWidthPx,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <LegendItem label="Forward" markerColor="rgba(0, 200, 83, 0.70)" />
              <LegendItem label="Backward" markerColor="rgba(255, 0, 0, 0.70)" />
              <LegendItem label="Unknown transition" markerColor="rgba(25, 118, 210, 0.80)" />
              <LegendItem label="Unknown line" markerColor="rgba(198, 40, 198, 0.82)" />
              <LegendItem label="Excessive" markerColor="rgba(255, 152, 0, 0.80)" />
              {showEstimatedLines ? <LegendLineItem label="Estimated line" /> : null}
            </Box>
          </Box>
        ) : null}

        {showFixationSlider && totalFixations > 0 ? (
          <FixationPlaybackControls
            value={sliderValue}
            total={totalFixations}
            onChange={onSliderChange}
            width={pageWidthPx}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function LegendItem({ label, markerColor }: { label: string; markerColor: string }) {
  return (
    <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Box
        style={{
          width: 12,
          height: 12,
          minWidth: 12,
          borderRadius: 999,
          background: markerColor,
          border: "1px solid rgba(0, 0, 0, 0.18)",
        }}
      />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Box>
  );
}

function LegendLineItem({ label }: { label: string }) {
  return (
    <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Box style={{ width: 18, minWidth: 18, borderTop: "2px dashed rgba(13, 110, 253, 0.70)" }} />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Box>
  );
}

const PLAYBACK_INTERVAL_MS = 120;

function FixationPlaybackControls({
  value,
  total,
  onChange,
  width,
}: {
  value: number;
  total: number;
  onChange: (v: number) => void;
  width: number;
}) {
  const [playing, setPlaying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const stop = useCallback(() => {
    setPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    stop();
    // If already at end, restart from 0
    if (valueRef.current >= total) onChange(0);
    setPlaying(true);
    intervalRef.current = setInterval(() => {
      const next = valueRef.current + 1;
      if (next > total) {
        stop();
        return;
      }
      onChange(next);
    }, PLAYBACK_INTERVAL_MS);
  }, [total, onChange, stop]);

  useEffect(() => stop, [stop]);

  const stepBack = useCallback(() => {
    stop();
    onChange(Math.max(0, valueRef.current - 1));
  }, [onChange, stop]);

  const stepForward = useCallback(() => {
    stop();
    onChange(Math.min(total, valueRef.current + 1));
  }, [total, onChange, stop]);

  const skipToEnd = useCallback(() => {
    stop();
    onChange(total);
  }, [total, onChange, stop]);

  return (
    <Box className="no-print" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box style={{ width, paddingTop: 8 }}>
        <Slider
          min={0}
          max={total}
          value={value}
          onChange={(v) => {
            if (playing) stop();
            onChange(v);
          }}
          label={null}
          size="sm"
          color="blue"
          thumbSize={14}
        />
        <Group justify="space-between" mt={6}>
          <Text
            size="xs"
            c="dimmed"
            fw={500}
            style={{ fontVariantNumeric: "tabular-nums", minWidth: 100 }}
          >
            Fixation {value} / {total}
          </Text>
          <Group gap={4}>
            <Tooltip label="Step back" withArrow position="top">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={stepBack}
                disabled={value === 0}
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={playing ? "Pause" : "Play"} withArrow position="top">
              <ActionIcon variant="light" color="blue" size="sm" onClick={playing ? stop : play}>
                {playing ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Step forward" withArrow position="top">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={stepForward}
                disabled={value >= total}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Show all" withArrow position="top">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={skipToEnd}
                disabled={value >= total}
              >
                <IconPlayerSkipForward size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>
    </Box>
  );
}

function drawSaccadeLines(
  ctx: CanvasRenderingContext2D,
  records: ReadingFixationRecord[],
  pageWidthPx: number,
  pageHeightPx: number,
) {
  if (records.length < 2) return;

  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  for (let i = 1; i < records.length; i++) {
    const from = percentagePointToCanvas(records[i - 1].position, pageWidthPx, pageHeightPx);
    const to = percentagePointToCanvas(records[i].position, pageWidthPx, pageHeightPx);
    ctx.strokeStyle = getMovementStrokeStyle(records[i].transitionDirection);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }
}

function drawFixationCircles(
  ctx: CanvasRenderingContext2D,
  records: ReadingFixationRecord[],
  durationSourceRecords: ReadingFixationRecord[],
  pageWidthPx: number,
  pageHeightPx: number,
  wordBoxMedianHeightPx: number,
) {
  const durations = durationSourceRecords
    .map((r) => r.durationSec)
    .filter((d) => Number.isFinite(d) && d > 0);
  const minDur = durations.length ? Math.min(...durations) : 0;
  const maxDur = durations.length ? Math.max(...durations) : 0;
  const base = Math.max(5, Math.min(14, wordBoxMedianHeightPx * 0.45));
  const minR = base * 0.65;
  const maxR = base * 2.2;

  records.forEach((record) => {
    const norm = normalize01(record.durationSec, minDur, maxDur);
    const r = lerp(minR, maxR, norm);
    const pos = percentagePointToCanvas(record.position, pageWidthPx, pageHeightPx);

    ctx.fillStyle = getFixationFillStyle(record.displayCategory);
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = "rgba(33, 37, 41, 0.25)";
    ctx.lineWidth = Math.max(1, Math.round(r * 0.12));
    ctx.stroke();

    const fontSizePx = Math.max(8, Math.min(12, Math.round(r * 0.7)));
    ctx.font = `700 ${fontSizePx}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
    ctx.strokeStyle = "rgba(0, 0, 0, 0.38)";
    ctx.lineWidth = Math.max(1, Math.round(fontSizePx * 0.14));
    ctx.strokeText(record.label, pos.x, pos.y);
    ctx.fillText(record.label, pos.x, pos.y);
  });
}

function drawEstimatedLines(
  ctx: CanvasRenderingContext2D,
  lines: EstimatedTextLine[],
  pageWidthPx: number,
  pageHeightPx: number,
) {
  ctx.save();
  ctx.lineWidth = 1;
  const baseFontSize = Math.max(10, Math.round(pageHeightPx * 0.028));
  ctx.setLineDash([8, 6]);

  lines.forEach((line) => {
    const leftPx = line.left01 * pageWidthPx;
    const rightPx = line.right01 * pageWidthPx;
    const centerYPx = line.centerY01 * pageHeightPx;

    ctx.strokeStyle = "rgba(13, 110, 253, 0.65)";
    ctx.beginPath();
    ctx.moveTo(leftPx, centerYPx);
    ctx.lineTo(rightPx, centerYPx);
    ctx.stroke();

    ctx.setLineDash([]);
    const labelFontSize = Math.max(8, Math.round(baseFontSize * 0.8));
    ctx.font = `600 ${labelFontSize}px sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fillText(`${line.index + 1}`, 2, clamp(centerYPx, 10, pageHeightPx - 10));
    ctx.setLineDash([8, 6]);
  });

  ctx.restore();
}

function getMovementStrokeStyle(direction: ReadingFixationTransitionDirection | string) {
  if (direction === "backward") return "rgba(255, 0, 0, 0.35)";
  if (direction === "unknown") return "rgba(25, 118, 210, 0.40)";
  return "rgba(0, 200, 83, 0.30)";
}

function getFixationFillStyle(category: ReadingFixationDisplayCategory) {
  if (category === "unknown_line") return "rgba(198, 40, 198, 0.34)";
  if (category === "backward") return "rgba(255, 0, 0, 0.30)";
  if (category === "unknown_transition") return "rgba(25, 118, 210, 0.32)";
  if (category === "excessive") return "rgba(255, 152, 0, 0.32)";
  return "rgba(0, 200, 83, 0.25)";
}

let sharedMeasureContext: CanvasRenderingContext2D | null | undefined;

function measureTextWidth(text: string, fontSizePx: number): number | null {
  if (typeof document === "undefined") return null;
  if (sharedMeasureContext === undefined) {
    sharedMeasureContext = document.createElement("canvas").getContext("2d");
  }
  if (!sharedMeasureContext) return null;
  sharedMeasureContext.font = `${fontSizePx}px ${WORD_FONT_STACK}`;
  return sharedMeasureContext.measureText(text).width;
}

function rectToCss(r: ReadingLayoutRect01, widthPx: number, heightPx: number) {
  return {
    left: r.x * widthPx,
    top: (1 - (r.y + r.h)) * heightPx,
    width: r.w * widthPx,
    height: r.h * heightPx,
  };
}

function computeContentWidth(outerWidth: number, ratio: number, maxWidth: number) {
  if (!outerWidth) return DEFAULT_FALLBACK_CONTENT_WIDTH;
  const target = outerWidth * ratio;
  if (!Number.isFinite(maxWidth) || maxWidth <= 0) {
    return Math.max(DEFAULT_MIN_CONTENT_WIDTH, target);
  }
  return clamp(target, DEFAULT_MIN_CONTENT_WIDTH, maxWidth);
}

function computePageHeight(contentWidth: number, aspect: number) {
  if (!contentWidth || !Number.isFinite(aspect) || aspect <= 0) return DEFAULT_FALLBACK_PAGE_HEIGHT;
  return Math.max(1, Math.round(contentWidth / aspect));
}

function orderSamples(samples: ReadingLayoutEyeSample[]) {
  return samples
    .map((s, idx) => ({ s, idx }))
    .sort((a, b) => (a.s.t ?? 0) - (b.s.t ?? 0) || a.idx - b.idx)
    .map(({ s }) => s);
}

function computeWordBoxMedianHeightPx(words: ReadingLayoutWord[], pageHeightPx: number) {
  const heights = words
    .map((word) => word?.r?.h)
    .filter((height): height is number => Number.isFinite(height) && height > 0)
    .map((height) => height * pageHeightPx)
    .sort((a, b) => a - b);

  if (!heights.length) return 12;
  return heights[Math.floor(heights.length / 2)];
}

function percentagePointToCanvas(point: PercentagePoint, widthPx: number, heightPx: number) {
  const x01 = clamp(point.xPercentage / 100, 0, 1);
  const y01 = clamp(point.yPercentage / 100, 0, 1);
  return { x: x01 * widthPx, y: y01 * heightPx };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function median(values: number[]) {
  const v = values
    .filter((x) => Number.isFinite(x))
    .slice()
    .sort((a, b) => a - b);
  if (!v.length) return Number.NaN;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 === 1 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function normalize01(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return 0.5;
  return clamp((value - min) / (max - min), 0, 1);
}

function estimateTextLines(words: ReadingLayoutWord[], _textOrientation: "L2R" | "R2L") {
  const layoutWords = words
    .map((word) => {
      const rect = word?.r;
      if (!rect) return null;

      const left01 = clamp(rect.x, 0, 1);
      const right01 = clamp(rect.x + rect.w, 0, 1);
      const top01 = clamp(1 - (rect.y + rect.h), 0, 1);
      const bottom01 = clamp(1 - rect.y, 0, 1);
      const height01 = bottom01 - top01;
      const centerY01 = top01 + height01 / 2;

      if (right01 <= left01 || bottom01 <= top01) return null;

      return { left01, right01, top01, bottom01, centerY01, height01 };
    })
    .filter(
      (
        w,
      ): w is {
        left01: number;
        right01: number;
        top01: number;
        bottom01: number;
        centerY01: number;
        height01: number;
      } => Boolean(w),
    )
    .sort((a, b) => a.centerY01 - b.centerY01 || a.left01 - b.left01);

  if (!layoutWords.length) return [];

  const medianHeight01 = median(layoutWords.map((w) => w.height01));
  const lineTolerance01 = Math.max(
    MIN_LINE_CLUSTER_TOLERANCE_01,
    Number.isFinite(medianHeight01) && medianHeight01 > 0
      ? medianHeight01 * LINE_CLUSTER_TOLERANCE_FACTOR
      : 0.02,
  );

  const clusteredLines: Array<Omit<EstimatedTextLine, "index">> = [];

  layoutWords.forEach((word) => {
    let bestLineIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;

    clusteredLines.forEach((line, lineIndex) => {
      const distance = Math.abs(word.centerY01 - line.centerY01);
      if (distance <= lineTolerance01 && distance < bestDistance) {
        bestDistance = distance;
        bestLineIndex = lineIndex;
      }
    });

    if (bestLineIndex === -1) {
      clusteredLines.push({
        centerY01: word.centerY01,
        top01: word.top01,
        bottom01: word.bottom01,
        left01: word.left01,
        right01: word.right01,
        wordCount: 1,
      });
      return;
    }

    const line = clusteredLines[bestLineIndex];
    const nextWordCount = line.wordCount + 1;
    line.centerY01 = (line.centerY01 * line.wordCount + word.centerY01) / nextWordCount;
    line.top01 = Math.min(line.top01, word.top01);
    line.bottom01 = Math.max(line.bottom01, word.bottom01);
    line.left01 = Math.min(line.left01, word.left01);
    line.right01 = Math.max(line.right01, word.right01);
    line.wordCount = nextWordCount;
  });

  return clusteredLines
    .sort((a, b) => a.centerY01 - b.centerY01)
    .map((line, index) => ({ index, ...line }));
}
